import React, { useMemo, useRef, useState } from "react";
import { ListJobsService } from "@application/useCases";
import { Batch, JobPipelineStepDetail, SoftwareEnumSchema, StatusEnumSchema } from "@domain/schemas/BI";
import { FailedBatchesDialog, FailedBatchLogEntry } from "./FailedBatchesDialog";
import { useTranslation } from "react-i18next";

type UseFailedBatchesDialogProperties = {
  listJobsService: ListJobsService;
  openLogs: (jobId: string, jobPipelineStepId: string, batch: { id: string; status?: StatusEnumSchema | null }) => Promise<void>;
};

type FailedBatchesTarget = {
  jobId: string;
  software?: SoftwareEnumSchema | null;
};

const isFailedBatchStatus = (status: StatusEnumSchema) => status === StatusEnumSchema.Failed || status === StatusEnumSchema.Aborted;

const hasKnownStepFailures = (step: JobPipelineStepDetail) => typeof step.failedBatchesCount === "number" && step.failedBatchesCount > 0;
const requiresBatchDerivedFailures = (step: JobPipelineStepDetail) => typeof step.failedBatchesCount !== "number";

export const useFailedBatchesDialog = ({ listJobsService, openLogs }: UseFailedBatchesDialogProperties) => {
  const { t: loc } = useTranslation();
  const [target, setTarget] = useState<FailedBatchesTarget | null>(null);
  const [entries, setEntries] = useState<FailedBatchLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  /**
   * Each open/close bumps the generation. Only the most recent request is allowed to write state, so a
   * slow load for a previously opened job can no longer overwrite the entries of the job shown now.
   */
  const requestGenerationReference = useRef(0);

  const closeDialog = () => {
    requestGenerationReference.current += 1;
    setTarget(null);
    setEntries([]);
    setErrorMessage(null);
    setIsLoading(false);
  };

  const openFailedBatchesDialog = async (jobId: string, software?: SoftwareEnumSchema | null) => {
    const requestGeneration = requestGenerationReference.current + 1;
    requestGenerationReference.current = requestGeneration;
    const isCurrentRequest = () => requestGenerationReference.current === requestGeneration;

    setTarget({ jobId, software });
    setEntries([]);
    setErrorMessage(null);
    setIsLoading(true);

    try {
      const steps = await listJobsService.getJobStepListAsync(jobId);
      const candidateSteps = steps.filter((step) => hasKnownStepFailures(step) || requiresBatchDerivedFailures(step));
      const failedEntriesByStep = await Promise.all(
        candidateSteps.map(async (step) => {
          const batchResponse = await listJobsService.getJobStepBatchListAsync(jobId, step.id);
          const failedBatches = (batchResponse.list ?? []).filter((batch): batch is Batch => Boolean(batch.id) && isFailedBatchStatus(batch.status));

          return failedBatches.map((batch) => ({
            step,
            batch: {
              id: batch.id,
              status: batch.status,
            },
          }));
        }),
      );

      if (!isCurrentRequest()) {
        return;
      }

      setEntries(failedEntriesByStep.flat());
    } catch {
      if (!isCurrentRequest()) {
        return;
      }

      setErrorMessage(loc("failedBatchLogsLoadError"));
    } finally {
      if (isCurrentRequest()) {
        setIsLoading(false);
      }
    }
  };

  const dialog = useMemo(() => {
    if (!target) {
      return null;
    }

    return (
      <FailedBatchesDialog
        entries={entries}
        software={target.software}
        isLoading={isLoading}
        errorMessage={errorMessage}
        onClose={closeDialog}
        onOpenLogs={(entry) => {
          closeDialog();
          void openLogs(target.jobId, entry.step.id, { id: entry.batch.id, status: entry.batch.status });
        }}
      />
    );
  }, [entries, errorMessage, isLoading, openLogs, target]);

  return {
    failedBatchesDialog: dialog,
    openFailedBatchesDialog,
  };
};
